import { BEIGE_300, BEIGE_600 } from 'constants/styling/theme'
import { InstructionProductList, ProductList } from './ProductList'
import { Trans, useTranslation } from 'react-i18next'

import { Box, CircularProgress } from '@mui/material'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MeasurementCostAssumption, useMeasurementCostAssumption } from './MeasurementCostAssumption'
import { Fragment, useEffect, useMemo } from 'react'
import { feeHasDiscount, formatPercentage, formatPrice, percentageValue, valueAfterDiscount } from 'utils/price'
import { useFormatterLanguage } from 'utils/hooks/useFormatterLanguage'

import Big from 'big.js'
import { MUIDivider } from 'components/common/MUIDivider'
import { OrderAdditionalContent } from './OrderAdditionalContent'
import { bigFromDecimal, bigFromFee } from 'utils/price/decimals'
import { OrderSummaryContainer } from './OrderSummaryContainer'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage/SystemMessage.component'
import { logger } from 'utils/logging'
import { useCart } from '../../_main/contexts'
import { useEmbedOneSystemSummary } from './useEmbedOneSystemSummary'
import { usePurchaseFlowConfig } from '../../_main/contexts/PurchaseFlowConfig.context'

/** Size of the centered reprice spinner shown over the summary while a live reprice is in flight. */
const REPRICE_SPINNER_SIZE = 24

/**
 * @component Cart (product summary) of PF selected products and instructions with connected charges
 * @example <Cart />
 */
export const Cart: React.FC = () => {
  const { t } = useTranslation(['order'])
  // vatRateLabel is formatted inside displayedTotal; re-derive it when the app language changes
  const formatterLanguage = useFormatterLanguage()
  const {
    cartProductList,
    total: {
      totalAfterDiscount,
      totalAfterVat,
      totalBeforeDiscount,
      totalVat,
      totalDiscount
    },
  } = useCart()

  const { catalogueCurrency, catalogueDiscount, catalogueVat, isRepricing } = usePurchaseFlowConfig()

  const {
    isOneSystemCart,
    hasCoveredLine,
    zwischensumme,
    partnerrabatt,
    percentageLabel,
  } = useEmbedOneSystemSummary()

  const { isActive: isMeasurementQuoteActive, measurementProductId, quote: measurementQuote, isFetching: isMeasurementQuoteFetching } = useMeasurementCostAssumption()
  const useQuoteTotals = isMeasurementQuoteActive && !!measurementQuote

  // A settled-but-quoteless active state means the customer sees catalogue fallback totals that omit the
  // engine surcharges — not an HTTP failure, so it is invisible to the transport-layer Sentry capture
  useEffect(() => {
    if (isMeasurementQuoteActive && !measurementQuote && !isMeasurementQuoteFetching) {
      logger.warn('Measurement quote unavailable — cart shows catalogue fallback totals', undefined, {
        context: { component: 'Cart' }
      })
    }
  }, [isMeasurementQuoteActive, measurementQuote, isMeasurementQuoteFetching])

  // The engine fills `net.discountPercentage` once backend ISSUE-3363 lands; until then the catalogue's
  // organization discount is the same number. Following the engine keeps the cart total and the
  // cost-assumption panel on one discount source.
  const quoteDiscount = useMemo(() => {
    if (!measurementQuote || !feeHasDiscount(measurementQuote.net)) return catalogueDiscount
    return bigFromDecimal(measurementQuote.net.discountPercentage)
  }, [measurementQuote, catalogueDiscount])

  // Drift tripwire: an engine discount that differs from the catalogue one means the panel/total and
  // the invoice can disagree again (ISSUE-3364 class) — surface it in Sentry instead of waiting for QA
  useEffect(() => {
    if (!useQuoteTotals || !measurementQuote || !feeHasDiscount(measurementQuote.net) || quoteDiscount.eq(catalogueDiscount)) return
    logger.warn('Measurement quote discount differs from catalogue discount', undefined, {
      context: { component: 'Cart', metadata: { quoteDiscount: quoteDiscount.toString(), catalogueDiscount: catalogueDiscount.toString() } }
    })
  }, [useQuoteTotals, measurementQuote, quoteDiscount, catalogueDiscount])

  // The quote replaces the measurement line only — other cart lines keep the catalogue pricing, so
  // the displayed totals swap the hidden line (base + options, org-discounted) for the quote amounts.
  // The engine quote is org-discounted exactly like every other line: placement stamps
  // `organization.discount` on the measurement lines and Stripe charges the discounted amount, so an
  // undiscounted preview would contradict the invoice (ISSUE-3364 / ISSUE-3307).
  const displayedTotal = useMemo(() => {
    if (!useQuoteTotals) return { totalBeforeDiscount, totalDiscount, totalAfterDiscount, totalVat, totalAfterVat, vatRateLabel: formatPercentage(catalogueVat ?? Big(0), formatterLanguage) }

    const measurementLine = cartProductList.find(product => product.id === measurementProductId)
    const measurementOptionsPrice = (measurementLine?.options ?? []).reduce((sum, option) => sum.plus(bigFromFee(option.feePrice).times(option.quantity ?? 0)), Big(0))
    const measurementLinePrice = measurementLine
      ? bigFromFee(measurementLine.feePrice).times(measurementLine.quantity || 0).plus(measurementOptionsPrice)
      : Big(0)

    const restBeforeDiscount = totalBeforeDiscount.minus(measurementLinePrice)
    const restAfterDiscount = valueAfterDiscount(restBeforeDiscount, catalogueDiscount)
    const restVat = percentageValue(restAfterDiscount, catalogueVat ?? Big(0))

    const quoteNet = bigFromFee(measurementQuote.net)
    const quoteNetAfterDiscount = valueAfterDiscount(quoteNet, quoteDiscount)
    // The quote's own `vat` is computed on the undiscounted net, so VAT is re-derived from the
    // discounted net at the quote's rate (the client's rate — it can differ from catalogueVat)
    const quoteVat = percentageValue(quoteNetAfterDiscount, Big(measurementQuote.vatRate))

    // Mirror the charge: the backend discounts the summed net once, applies VAT and rounds the gross half
    // up to cents for Stripe (`ConcreteFee.asSmallestUnit`, ISSUE-4066). Rounding net and gross the same way
    // here and deriving the VAT and discount rows from them keeps every row reconciling AND the gross equal
    // to the charge. That last part holds only while catalogueVat and the quote's rate agree: the backend
    // charges one rate (`VatUtils.vatRate(invoicingAddress)`) on the whole discounted net.
    const net = restAfterDiscount.plus(quoteNetAfterDiscount).round(2, Big.roundHalfUp)
    const gross = restAfterDiscount.plus(restVat).plus(quoteNetAfterDiscount).plus(quoteVat).round(2, Big.roundHalfUp)
    const vat = gross.minus(net)

    // The displayed VAT can blend two rates (rest-of-cart at catalogueVat, quote at its own rate) — label
    // the quote rate only while it covers the whole amount, otherwise show the effective combined rate
    const isSingleVatRate = restAfterDiscount.lte(0) || (catalogueVat ?? Big(0)).eq(measurementQuote.vatRate)
    // Same formatter as the discount label so the two percent labels agree ("19" next to "8,5", not "19.00")
    const vatRateLabel = isSingleVatRate || net.lte(0)
      ? formatPercentage(Big(measurementQuote.vatRate), formatterLanguage)
      : formatPercentage(vat.div(net).times(100), formatterLanguage)

    return {
      totalBeforeDiscount: restBeforeDiscount.plus(quoteNet),
      totalDiscount: restBeforeDiscount.plus(quoteNet).minus(net),
      totalAfterDiscount: net,
      totalVat: vat,
      totalAfterVat: gross,
      vatRateLabel
    }
  }, [useQuoteTotals, cartProductList, measurementProductId, measurementQuote, catalogueDiscount, quoteDiscount, catalogueVat, totalBeforeDiscount, totalDiscount, totalAfterDiscount, totalVat, totalAfterVat, formatterLanguage])

  return (
    <Stack gap={2} data-cy="cart">

      {/** SUMMARY SECTION */}
      <SectionedBorderBox title={t('cart.summary')}>
        {/* Relative anchor so the reprice overlay can cover the whole summary without shifting layout. */}
        <Box position="relative">

          {/** LIVE REPRICE OVERLAY (embed only, isRepricing is always false outside embed): lightens the
           * summary and centers a spinner while the server recalculates OneSystem prices. The summary
           * content stays mounted and visible underneath. */}
          {isRepricing &&
            <Box
              data-testid="cart-reprice-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              <CircularProgress size={REPRICE_SPINNER_SIZE} thickness={4} data-testid="cart-reprice-spinner" />
            </Box>
          }

          {/** MEASUREMENT COST ASSUMPTION (On-Site Area Report redesign, ISSUE-2188) */}
          <MeasurementCostAssumption />

          {/** PRODUCT LIST */}
          <ProductList hiddenProductIds={useQuoteTotals && measurementProductId ? [measurementProductId] : undefined} />

          {/* INSTRUCTION PRODUCT LIST */}
          <InstructionProductList />

          {/** ONE SYSTEM CONTRACT BANNER (embed-only, once a covered pack is in the cart) */}
          {hasCoveredLine &&
            <SystemMessage
              variant="success"
              message={t('cart.one_system_included_banner')}
            />
          }

          <MUIDivider margin={12} />

          {/** SUB-TOTAL (with discount) */}
          {catalogueDiscount.gt(0) &&
            <Fragment>

              <Stack gap={1}>

                {/** Sub-total */}
                <OrderSummaryContainer
                  type="subtotal"
                  summaryText={t('cart.sub_total')}
                  price={formatPrice(displayedTotal.totalBeforeDiscount, catalogueCurrency)}
                />

                <BorderBoxWrapper
                  padding={1}
                  elevation="none"
                  justifyContent="flex-end"
                  borderColor={BEIGE_600}
                  backgroundColor={BEIGE_300}
                >
                  {/** Total discount */}
                  <OrderSummaryContainer
                    type='discount'
                    summaryText={
                      <Trans
                        t={t}
                        i18nKey="cart.special_discount"
                        values={{ percentage: formatPercentage(catalogueDiscount) }}
                      />
                    }
                    price={`-${formatPrice(displayedTotal.totalDiscount, catalogueCurrency)}`}
                  />
                </BorderBoxWrapper>

              </Stack>

              <MUIDivider margin={12} />

            </Fragment>
          }

          {/** ONE SYSTEM ZWISCHENSUMME + PARTNERRABATT (embed-only, presentational) */}
          {isOneSystemCart &&
            <Fragment>

              <Stack gap={1}>

                {/** Zwischensumme (reuses the sub_total key) */}
                <OrderSummaryContainer
                  type="subtotal"
                  summaryText={t('cart.sub_total')}
                  price={formatPrice(zwischensumme, catalogueCurrency)}
                />

                <BorderBoxWrapper
                  padding={1}
                  elevation="none"
                  justifyContent="flex-end"
                  borderColor={BEIGE_600}
                  backgroundColor={BEIGE_300}
                >
                  {/** Partnerrabatt */}
                  <OrderSummaryContainer
                    type="discount"
                    summaryText={
                      <Trans
                        t={t}
                        i18nKey="cart.partner_discount"
                        values={{ percentage: percentageLabel }}
                      />
                    }
                    price={`-${formatPrice(partnerrabatt, catalogueCurrency)}`}
                  />
                </BorderBoxWrapper>

              </Stack>

              <MUIDivider margin={12} />

            </Fragment>
          }

          {/** TOTAL */}
          <Stack direction="column">

            {/** Total of order */}
            <OrderSummaryContainer
              summaryText={t('cart.total')}
              price={formatPrice(displayedTotal.totalAfterDiscount, catalogueCurrency)}
              type="total"
            />

            <Stack alignItems="flex-end" gap={1}>
              {/** Total VAT */}
              <OrderSummaryContainer
                summaryText={`${t('cart.vat')} (${displayedTotal.vatRateLabel}%)`}
                price={formatPrice(displayedTotal.totalVat, catalogueCurrency)}
              />

              {/** Total after VAT */}
              <OrderSummaryContainer
                summaryText={t('cart.after_vat')}
                price={formatPrice(displayedTotal.totalAfterVat, catalogueCurrency)}
              />
            </Stack>

          </Stack>

          {/** ADDITIONAL SECTION */}
          <OrderAdditionalContent />

        </Box>
      </SectionedBorderBox>

    </Stack>
  )
}
